import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtQty } from "../api";
import { Plus, Factory, MinusCircle, FileCheck, Package, AlertTriangle } from "lucide-react";
import StatusBadge from "../components/StatusBadge";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Loading dashboard...</div>;

  const kpis = [
    { label: "Finished Goods Stock", value: fmtQty(data.finished_goods_total), testid: "kpi-finished-goods", to: "/inventory?type=Finished Good" },
    { label: "Raw Material Stock", value: fmtQty(data.raw_materials_total), testid: "kpi-raw-materials", to: "/inventory?type=Raw Material" },
    { label: "Today's Production", value: fmtQty(data.today_production), testid: "kpi-today-production", to: "/manufacturing/orders" },
    { label: "Pending QC Lots", value: data.pending_qc, testid: "kpi-pending-qc", to: "/manufacturing/lots?qc=Pending" },
  ];

  const actions = [
    { label: "Add Inventory", icon: Plus, to: "/inventory", testid: "qa-add-inventory" },
    { label: "New Manufacturing Order", icon: Factory, to: "/manufacturing/orders/new", testid: "qa-new-mo" },
    { label: "Stock Deduction", icon: MinusCircle, to: "/inventory?tab=deductions", testid: "qa-stock-deduction" },
    { label: "Create Packing List", icon: FileCheck, to: "/packing-list/new", testid: "qa-new-packing-list" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <button key={k.label} data-testid={k.testid} onClick={() => navigate(k.to)}
            className="sk-card p-5 text-left hover:shadow-md transition-shadow">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className="mt-2 font-heading text-3xl font-extrabold text-[#002B5D]">{k.value}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        {actions.map((a) => (
          <Link key={a.label} to={a.to} data-testid={a.testid}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-heading font-bold text-sm text-white transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: "#002B5D" }}>
            <a.icon className="w-4 h-4" /> {a.label}
          </Link>
        ))}
      </div>

      {data.low_stock.length > 0 && (
        <div className="sk-card border-l-4 p-5" style={{ borderLeftColor: "#EB1C27" }} data-testid="low-stock-alert">
          <div className="flex items-center gap-2 font-heading font-bold text-[#002B5D]">
            <AlertTriangle className="w-5 h-5 text-[#EB1C27]" /> Low Stock
          </div>
          <div className="mt-3 space-y-1 text-sm">
            {data.low_stock.map((p) => (
              <div key={p.code} className="flex justify-between max-w-md">
                <span>{p.name} <span className="font-mono text-slate-400">({p.code})</span></span>
                <span className="font-mono text-rose-600">{fmtQty(p.stock)} {p.uom} / min {fmtQty(p.min_stock)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="sk-card" data-testid="recent-mos">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="font-heading font-bold text-[#002B5D]">Recent Manufacturing Orders</h3>
            <Link to="/manufacturing/orders" className="text-sm text-[#EB1C27] font-semibold hover:underline">View all</Link>
          </div>
          <table className="w-full sk-table">
            <thead><tr><th>MO</th><th>Product</th><th>Qty</th><th>Status</th></tr></thead>
            <tbody>
              {data.recent_mos.length === 0 && <tr><td colSpan={4} className="text-slate-400 text-center">No orders yet</td></tr>}
              {data.recent_mos.map((m) => (
                <tr key={m.id} className="cursor-pointer" onClick={() => navigate(`/manufacturing/orders/${m.id}`)} data-testid={`dash-mo-${m.mo_number}`}>
                  <td className="font-mono font-semibold text-[#002B5D]">{m.mo_number}</td>
                  <td>{m.product_name}</td>
                  <td className="font-mono">{fmtQty(m.planned_qty)} {m.uom}</td>
                  <td><StatusBadge status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sk-card" data-testid="recent-lots">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="font-heading font-bold text-[#002B5D]">Recent Lots</h3>
            <Link to="/manufacturing/lots" className="text-sm text-[#EB1C27] font-semibold hover:underline">View all</Link>
          </div>
          <table className="w-full sk-table">
            <thead><tr><th>Lot</th><th>Product</th><th>Available</th><th>QC</th></tr></thead>
            <tbody>
              {data.recent_lots.length === 0 && <tr><td colSpan={4} className="text-slate-400 text-center">No lots yet</td></tr>}
              {data.recent_lots.map((l) => (
                <tr key={l.id} className="cursor-pointer" onClick={() => navigate(`/lots/${l.id}`)} data-testid={`dash-lot-${l.lot_number}`}>
                  <td className="font-mono font-semibold text-[#002B5D]">{l.lot_number}</td>
                  <td>{l.product_name}</td>
                  <td className="font-mono">{fmtQty(l.current_qty)} {l.uom}</td>
                  <td><StatusBadge status={l.qc?.status || "Pending"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
